import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DirectoryOccurrenceFeed } from './DirectoryOccurrenceFeed';
import { usePersonOccurrences } from './hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';

interface PersonEventFeedProps {
    personId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function PersonEventFeed({
    personId,
    titleContent,
    emptyState,
}: PersonEventFeedProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const navigate = useNavigate();
    const {
        data: occurrences,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
    } = usePersonOccurrences({
        personId,
        pageSize: 20,
    });

    const handleViewAll = useCallback(() => {
        navigate(`${AppRoutes.EVENTS}?personId=${personId}`);
    }, [navigate, personId]);

    return (
        <DirectoryOccurrenceFeed
            occurrences={occurrences}
            isLoading={isLoading}
            containerTitle={t('personPage.events')}
            navigationFrom="person-events"
            feedContext={{ type: 'person', personId }}
            onViewAll={handleViewAll}
            titleContent={titleContent}
            emptyState={emptyState}
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
        />
    );
}
